/**
 * Reusable selectors for derived game state
 * For cache data access, use hooks from @/state/cacheStoreHooks
 */

import { useShallow } from "zustand/react/shallow";
import { AnimationStep, HistoryEntry, OverlayType, Stats } from "@/types/state";
import { useFinanceTypes, useGameConfiguration, usePhases } from "@/state/cacheStoreHooks";
import { FinanceType, Item, Message as GqlMessage, Phase } from "@/types/api";
import { getItemsFromPhase } from "@/utils/itemUtils";
import { useGameStore } from "@/state/gameStore";
import {useMemo, useRef} from "react";

// -----------------------------------------------------------------------------
// Game State Selectors
// -----------------------------------------------------------------------------

export function useStats(): Stats {
    return useGameStore((state) => state.stats);
}

export function useInventory(): string[] {
    return useGameStore((state) => state.inventory);
}

export function useUnlockedAchievements(): string[] {
    return useGameStore((state) => state.unlockedAchievements);
}

// -----------------------------------------------------------------------------
// UI State Selectors
// -----------------------------------------------------------------------------
export function useActiveOverlay(): OverlayType {
    return useGameStore((state) => state.activeOverlay);
}

// -----------------------------------------------------------------------------
// Current Context Selectors
// -----------------------------------------------------------------------------
export function useCompletedPhaseMessages(): number {
    return useGameStore((state) => state.completedPhaseMessages);
}

export function useCurrentPhaseItems(): Item[] {
    const phase = useCurrentPhase();

    if (!phase) return [];
    return getItemsFromPhase(phase);
}

/**
 * Get the current phase data (resolved from cache)
 */
export function useCurrentPhase(): Phase | null {
    const phaseId = useGameStore((state) => state.currentPhaseId);
    const phases = usePhases();

    if (!phaseId) {
        return null;
    }
    return phases.find((phase) => phase.documentId === phaseId) ?? null;
}

export function useNextPhase(): Phase | null {
    const currentPhaseId = useGameStore((state) => state.currentPhaseId);
    const phases = usePhases();

    if (!currentPhaseId || phases.length === 0) return null;

    const currentIndex = phases.findIndex((phase) => phase.documentId === currentPhaseId);
    if (currentIndex === -1 || currentIndex === phases.length - 1) {
        return null;
    }
    return phases[currentIndex + 1];
}

/**
 * Get the index of the current phase (0-based)
 */
export function useCurrentPhaseIndex(): number {
    const phaseId = useGameStore((state) => state.currentPhaseId);
    const phases = usePhases();

    if (!phaseId || phases.length === 0) return 0;

    const index = phases.findIndex((phase) => phase.documentId === phaseId);
    return index === -1 ? 0 : index;
}

/**
 * Get the current room data (resolved from cache based on selectedRoom)
 */
export function useCurrentRoom() {
    const selectedRoom = useGameStore((state) => state.currentRoomVariant);
    const phase = useCurrentPhase();

    if (!phase || !selectedRoom) return null;

    return selectedRoom === "a" ? phase.room_a : phase.room_b;
}

/**
 * Get the current message from the message queue (ephemeral state)
 */
export function useCurrentMessage(): GqlMessage | null {
    return useGameStore((state) => state.currentMessage);
}

// this should only be used for the game end screen, as it relies on game over reason and fallback finance types defined in game configuration
// or a finance type being set
export function usePlayersFinanceType(): FinanceType | null {
    const financeTypeId = useGameStore((state) => state.financeTypeId);
    const financeTypes = useFinanceTypes();
    const gameOverReason = useGameStore((state) => state.gameOverReason);
    const gameConfig = useGameConfiguration();

    if (!gameConfig?.gameOverBrokeFallbackFinanceType || !gameConfig.gameOverSadFallbackFinanceType) {
        throw new Error("Game configuration is missing required fallback finance types");
    }

    if (!financeTypeId && gameOverReason !== "none") {
        if (gameOverReason === "cash") {
            return gameConfig.gameOverBrokeFallbackFinanceType;
        }
        return gameConfig.gameOverSadFallbackFinanceType;
    }

    const financeType = useMemo(
        () => (financeTypeId ? (financeTypes.find((ft) => ft?.documentId === financeTypeId) ?? null) : null),
        [financeTypeId, financeTypes],
    );

    return financeType || null;
}

// -----------------------------------------------------------------------------
// Message Visibility
// -----------------------------------------------------------------------------

/**
 * Single source of truth for whether the message component should be visible.
 * Message is visible when:
 * - A current message exists
 * - No response is being processed (exit animation)
 * - No post-response animations are playing
 * - No queue timer is running (inter-message delay)
 */
export function useIsMessageVisible(): boolean {
    return useGameStore(
        (state) =>
            state.currentMessage !== null &&
            state.pendingResponse === null &&
            state.currentPostMessageAnimationStep === null &&
            !state.queueTimerActive,
    );
}

// -----------------------------------------------------------------------------
// Animation Sequencer Selectors
// -----------------------------------------------------------------------------

export function useCurrentAnimationStep(): AnimationStep | null {
    return useGameStore((state) => state.currentPostMessageAnimationStep);
}

export function useDarkOverlayVisible(): boolean {
    return useGameStore((state) => state.darkOverlayVisible);
}

// -----------------------------------------------------------------------------
// Timeline Selectors
// -----------------------------------------------------------------------------

type TimelineProgress = {
    phaseIndex: number;
    totalPhases: number;
    completedMessages: number;
    totalMessages: number;
    overallProgress: number;
};

export function useTimelineProgress(): TimelineProgress {
    const completedPhaseMessages = useCompletedPhaseMessages();
    const phaseIndex = useCurrentPhaseIndex();
    const phases = usePhases();

    const timerElapsed = useGameStore((state) => state.queueTimerElapsed);
    const timerDuration = useGameStore((state) => state.queueTimerDuration);
    const effectivePhaseMessageTotal = useGameStore((state) => state.effectivePhaseMessageTotal);

    const maxProgressRef = useRef(0);

    const totalPhases = phases.length;
    const totalDefinedMessages = phases.reduce((sum, phase) => sum + phase.messages.length, 0);
    const phasesBeforeMessages = phases.slice(0, phaseIndex).reduce((sum, phase) => sum + phase.messages.length, 0);
    const currentPhaseDefinedCount = phases[phaseIndex]?.messages.length ?? 0;

    const timerContribution = timerDuration > 0
        ? Math.min(timerElapsed / timerDuration, 1)
        : 0;

    const smoothCompleted = completedPhaseMessages + timerContribution;
    const withinPhaseProgress = effectivePhaseMessageTotal > 0
        ? Math.min(smoothCompleted / effectivePhaseMessageTotal, 1)
        : 0;

    const phaseRange = totalDefinedMessages > 0
        ? currentPhaseDefinedCount / totalDefinedMessages
        : 0;
    const phaseStartProgress = totalDefinedMessages > 0
        ? phasesBeforeMessages / totalDefinedMessages
        : 0;

    const overallProgress = Math.min(phaseStartProgress + phaseRange * withinPhaseProgress, 0.999);

    // The slider progress should never decrease. This can happen when the last
    // message in a phase has a follow-up: the slider has already reached the
    // phase boundary, but effectivePhaseMessageTotal then increases, which would
    // pull the calculated progress backward. Clamping to the max seen value
    // prevents any visible backward jump.
    console.log(maxProgressRef.current, overallProgress);
    maxProgressRef.current = Math.max(overallProgress, maxProgressRef.current);

    return {
        phaseIndex,
        totalPhases,
        completedMessages: phasesBeforeMessages + completedPhaseMessages,
        totalMessages: totalDefinedMessages,
        overallProgress: maxProgressRef.current,
    };
}

// -----------------------------------------------------------------------------
// History Selectors
// -----------------------------------------------------------------------------

export function useCashHistory(): HistoryEntry[] {
    return useGameStore(useShallow((state) => (state.history ?? []).filter((e) => e.type === "cash")));
}

export function useDepotHistory(): HistoryEntry[] {
    return useGameStore(useShallow((state) => (state.history ?? []).filter((e) => e.type === "depot")));
}
